// Thumbs.js — per-AREA 👍/👎 for the Layered Read.
//
// Purpose: the Layered Read is several separately-generated reads stitched together (script findings,
// axis inspectors, per-unit craft reads, pattern rollups, the "what happens" recaps). They do NOT all
// work equally well. Tagging each one with its own div_id lets us compare them:
//
//     SELECT div_id, feedback_type, COUNT(*) FROM thumb_feedback
//     WHERE div_id LIKE 'lr:%' GROUP BY div_id, feedback_type;
//
// Wire protocol: the SAME contract the legacy thumbs use (feedback_include.php → POST /feedback →
// ThumbFeedbackController::store → thumb_feedback + email to lemuel). Form-encoded, CSRF-exempt
// (VerifyCsrfToken::$except has 'feedback'). Two posts, like the legacy flow: the thumb lands
// immediately (so we never lose the signal if they don't type), the optional note is a second row.
//
// The URL matters: the controller derives movie_id ONLY from ?share_id / ?movieid / ?submitted_id, and
// the admin per-movie thumbs view queries `url LIKE '%<hash>%'`. The embed lives on /view_analysis?id=<int>,
// which satisfies neither — so we append ?movieid=<hash> to the reported URL when it isn't already there.

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { tip } from './Tip';

const NAVY = '#14536e';

const reportUrl = (movieHash) => {
  let href = '';
  try { href = window.location.href; } catch (e) { href = ''; }
  if (!movieHash) return href;
  if (/[?&](movieid|share_id|submitted_id)=/.test(href)) return href;
  return href + (href.includes('?') ? '&' : '?') + 'movieid=' + encodeURIComponent(movieHash);
};

// div_id column is 50 chars (controller Str::limit(…, 50)) — keep keys short and prefix-groupable.
export const thumbKey = (...parts) => parts.filter(p => p !== null && p !== undefined && p !== '').join(':').slice(0, 50);

function send(area, type, text, movieHash) {
  const data = {
    url: reportUrl(movieHash),
    divId: String(area).slice(0, 50),
    feedbackType: type,
    feedbackText: text || '',
  };
  const body = Object.keys(data).map(k => encodeURIComponent(k) + '=' + encodeURIComponent(data[k])).join('&');
  return fetch('/feedback', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
    body,
  });
}

// Material thumb_up path; the down thumb is the same path rotated 180° about its centre.
const THUMB_D = 'M2 21h4V9H2v12zM23 10a2 2 0 0 0-2-2h-6.31l.95-4.57.03-.32a1.5 1.5 0 0 0-.44-1.06L14.17 1 7.59 7.59A2 2 0 0 0 7 9v10a2 2 0 0 0 2 2h9a2 2 0 0 0 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z';

const ThumbIcon = ({ down, size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ display: 'block' }}>
    <path d={THUMB_D} fill="currentColor" transform={down ? 'rotate(180 12 12)' : undefined} />
  </svg>
);
ThumbIcon.propTypes = { down: PropTypes.bool, size: PropTypes.number };

/**
 * @param area      div_id — the thing being rated (e.g. 'lr:axis:contract', 'lr:read:scene:12')
 * @param movieHash movie.hash — carried in the reported URL so the row resolves to a movie
 * @param label     the question ("Was this read useful?")
 * @param compact   inline variant (no top rule, smaller) for rating a block inside a pane
 * @param onTrack   optional telemetry hook, called with (action, metadata)
 *
 * Remount (key={area}) to reset — Icicle does exactly that when the pane changes.
 */
export default function Thumbs({ area, movieHash = null, label = 'Was this useful?', compact = false, onTrack = null }) {
  const [sent, setSent] = useState(null);      // 'up' | 'down' — the thumb already posted
  const [note, setNote] = useState('');
  const [noteSent, setNoteSent] = useState(false);

  if (!area) return null;

  const click = (type) => {
    if (sent) return;
    setSent(type);
    send(area, type, '', movieHash).catch(() => { /* never break the read over feedback */ });
    if (onTrack) onTrack('ic_thumb', { area, thumb: type });
  };

  const submitNote = () => {
    const t = note.trim();
    if (!t || !sent) return;
    send(area, sent, t, movieHash).catch(() => {});
    setNoteSent(true);
    if (onTrack) onTrack('ic_thumb_note', { area, thumb: sent });
  };

  const btn = (active) => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: compact ? 26 : 30, height: compact ? 26 : 30,
    border: `1.5px solid ${active ? NAVY : '#cbd5e1'}`, borderRadius: 8,
    background: active ? NAVY : '#fff', color: active ? '#fff' : '#64748b',
    cursor: sent ? 'default' : 'pointer', padding: 0,
  });

  const wrap = compact
    ? { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginTop: '.5rem' }
    : { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10,
        margin: '1.4rem 0 .2rem', padding: '.75rem 0 0', borderTop: '1px solid #e7edf1' };

  return (
    <div style={wrap} data-thumbs={area}>
      <span style={{ color: '#475569', fontSize: compact ? '.82rem' : '.88rem', fontWeight: 600 }}>
        {sent ? 'Thanks — anything you’d add?' : label}
      </span>

      {!sent && (
        <>
          <button {...tip('This was useful')} type="button" onClick={() => click('up')} style={btn(false)} aria-label="Thumbs up">
            <ThumbIcon size={compact ? 13 : 15} />
          </button>
          <button {...tip('This missed')} type="button" onClick={() => click('down')} style={btn(false)} aria-label="Thumbs down">
            <ThumbIcon down size={compact ? 13 : 15} />
          </button>
        </>
      )}

      {sent && !noteSent && (
        <>
          <span style={btn(true)} aria-hidden="true"><ThumbIcon down={sent === 'down'} size={compact ? 13 : 15} /></span>
          <input
            type="text" value={note} onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitNote(); } }}
            placeholder={sent === 'down' ? 'What was off? (optional)' : 'What worked? (optional)'}
            style={{ flex: '1 1 180px', minWidth: 140, maxWidth: 360, border: '1px solid #cbd5e1', borderRadius: 8,
              padding: '.35rem .55rem', fontSize: '.85rem', color: '#0f172a', outline: 'none' }}
          />
          <button type="button" onClick={submitNote} disabled={!note.trim()}
            style={{ border: 'none', borderRadius: 8, padding: '.38rem .8rem', fontSize: '.82rem', fontWeight: 700,
              background: note.trim() ? NAVY : '#e2e8f0', color: note.trim() ? '#fff' : '#94a3b8',
              cursor: note.trim() ? 'pointer' : 'default' }}>
            Send
          </button>
        </>
      )}

      {noteSent && <span style={{ color: NAVY, fontSize: '.85rem', fontWeight: 700 }}>Thanks — noted.</span>}
    </div>
  );
}

Thumbs.propTypes = {
  area: PropTypes.string,
  movieHash: PropTypes.string,
  label: PropTypes.string,
  compact: PropTypes.bool,
  onTrack: PropTypes.func,
};
